/**
 * Configuration queue — pick an SGS build line, alternate order, or inventory alternate before clocking in.
 * Labor starts on the work routes only (`ConfigurationWorkPage`).
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Loader2, RefreshCw } from "lucide-react";

import { BatchTeamNotesGlyph } from "@/components/manufacturing/BatchTeamNotesGlyph";
import { Button } from "@/components/ui/Button";
import { FilterChip } from "@/components/ui/FilterChip";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useAuth } from "@/components/AuthProvider";
import { buildManufacturingBatchDetailsPath } from "@/lib/manufacturingEditBatchPath";
import { formatKitQuantity } from "@/lib/formatKitQuantity";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { PrePackCartonOperatorStack } from "@/pages/prepack/PrePackCartonOperatorStack";

import {
  compareConfigurationQueueItems,
  CONFIGURATION_QUEUE_QUERY_KEY,
  configurationActiveJobOperatorsQueryKey,
  CONFIGURATION_JOB_PRESENCE_INTERVAL_MS,
  configurationQueueRowKey,
  fetchConfigurationActiveJobOperators,
  fetchConfigurationQueue,
  type ConfigurationQueueItem,
} from "./configurationApi";
import {
  CONFIGURATION_QUEUE_TLA_FILTER_OPTIONS,
  configurationQueueItemMatchesTlaFilters,
  type ConfigurationQueueTlaFilterKey,
} from "./configurationQueueTlaFilter";
import { filterConfigurationOperatorsForQueueItem } from "./configurationJobPresence";
import { CONFIGURATION_LABOR_FLASH_STORAGE_KEY } from "./configurationLaborFlash";
import { ConfigurationAlternateModal } from "./ConfigurationAlternateModal";
import { DeviceLookupModalTrigger } from "./DeviceLookupModal";
import {
  configurationAlternateOrderPath,
  configurationBuildPath,
} from "./configurationNavigation";
import { configurationQueueRowRemainingLine } from "./configurationQueueRowMeta";

const PAGE_HELP =
  "Configuration reproduces the Manufacturing-Dashboard Test Team SGS assembly + rebuild workflow in the hub. " +
  "Pick a build line or alternate order from the queue, or configure an ad-hoc inventory alternate, then open the work page to connect SDTestApp and clock in. " +
  "This list does not start labor — only the work page does, matching Pre-Pack and Clamp quality control. " +
  "Each row shows SKU, quantity remaining, and target date on line 1; batch link and team notes (or alternate order id) on line 2. " +
  "Alternate orders created elsewhere appear in this list sorted by date alongside batch lines. " +
  "Wireless units (FI-5A-W*, DM-1*) update the wireless portal on submit. Labor is recorded under the Config station.";

export function ConfigurationQueuePage(): JSX.Element {
  const [alternateModalOpen, setAlternateModalOpen] = useState(false);
  const [laborReturnFlash, setLaborReturnFlash] = useState<string | null>(null);
  const [selectedTlaFilters, setSelectedTlaFilters] = useState<Set<ConfigurationQueueTlaFilterKey>>(
    () => new Set(),
  );
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const { session } = useAuth();
  const viewerUserId = session?.user?.id ?? null;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CONFIGURATION_LABOR_FLASH_STORAGE_KEY);
      if (raw != null && raw.trim() !== "") {
        sessionStorage.removeItem(CONFIGURATION_LABOR_FLASH_STORAGE_KEY);
        setLaborReturnFlash(raw.trim());
      }
    } catch {
      /* private mode or storage unavailable */
    }
  }, []);

  const queueQuery = useQuery({
    queryKey: CONFIGURATION_QUEUE_QUERY_KEY,
    queryFn: fetchConfigurationQueue,
    staleTime: 30_000,
  });

  const operatorsQuery = useQuery({
    queryKey: configurationActiveJobOperatorsQueryKey,
    queryFn: fetchConfigurationActiveJobOperators,
    staleTime: CONFIGURATION_JOB_PRESENCE_INTERVAL_MS,
    refetchInterval: CONFIGURATION_JOB_PRESENCE_INTERVAL_MS,
  });

  const queueItems = useMemo(() => {
    const items = [...(queueQuery.data?.items ?? [])];
    items.sort(compareConfigurationQueueItems);
    return items;
  }, [queueQuery.data?.items]);

  const filteredQueueItems = useMemo(
    () => queueItems.filter((item) => configurationQueueItemMatchesTlaFilters(item.requiredTlaSku, selectedTlaFilters)),
    [queueItems, selectedTlaFilters],
  );

  const tlaFiltersActive = selectedTlaFilters.size > 0;

  const toggleTlaFilter = (key: ConfigurationQueueTlaFilterKey): void => {
    setSelectedTlaFilters((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const clearTlaFilters = (): void => {
    setSelectedTlaFilters(new Set());
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Configuration</PageTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          <DeviceLookupModalTrigger />
          <Button type="button" onClick={() => setAlternateModalOpen(true)}>
            Configure alternate
          </Button>
          <Button
            size="sm"
            variant="secondary"
            type="button"
            onClick={() => void queueQuery.refetch()}
            disabled={queueQuery.isFetching}
          >
            {queueQuery.isFetching ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {laborReturnFlash ? (
        <div
          className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900"
          role="status"
        >
          <p className="min-w-0 flex-1 leading-snug">{laborReturnFlash}</p>
          <Button size="sm" variant="secondary" type="button" onClick={() => setLaborReturnFlash(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}

      {queueQuery.isLoading ? (
        <p className="text-sm text-slate-500">
          <Loader2 className="mr-1 inline h-4 w-4 animate-spin" aria-hidden /> Loading SGS queue...
        </p>
      ) : queueQuery.isError ? (
        <p className="text-sm text-red-700">Could not load the SGS queue.</p>
      ) : queueItems.length === 0 ? (
        <p className="text-sm text-slate-500">No serialized SGS build lines or alternate orders are waiting.</p>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">TLA</span>
              {CONFIGURATION_QUEUE_TLA_FILTER_OPTIONS.map((option) => {
                const isOn = selectedTlaFilters.has(option.key);
                return (
                  <FilterChip
                    key={option.key}
                    pressed={isOn}
                    onClick={() => toggleTlaFilter(option.key)}
                  >
                    {option.label}
                  </FilterChip>
                );
              })}
              {tlaFiltersActive ? (
                <Button type="button" size="sm" variant="ghost" onClick={clearTlaFilters}>
                  Clear TLA filters
                </Button>
              ) : null}
            </div>
            {tlaFiltersActive ? (
              <p className="text-sm text-slate-600">
                Showing {formatKitQuantity(filteredQueueItems.length)} of {formatKitQuantity(queueItems.length)} queue
                rows for selected TLAs.
              </p>
            ) : null}
          </div>

          {filteredQueueItems.length === 0 ? (
            <p className="text-sm text-slate-500">No rows match these TLA filters.</p>
          ) : (
            <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
              {filteredQueueItems.map((item) => (
                <ConfigurationQueueRow
                  key={configurationQueueRowKey(item)}
                  item={item}
                  hubWritesAllowed={hubWritesAllowed}
                  operators={filterConfigurationOperatorsForQueueItem(operatorsQuery.data ?? [], item)}
                  viewerUserId={viewerUserId}
                />
              ))}
            </ul>
          )}
        </>
      )}

      <ConfigurationAlternateModal open={alternateModalOpen} onClose={() => setAlternateModalOpen(false)} />
    </div>
  );
}

function formatMfrShipByDate(raw: string | null): string {
  if (raw == null || raw.trim() === "") return "—";
  const parsed = new Date(raw.includes("T") ? raw : `${raw.trim()}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return raw.trim();
  if (parsed.getFullYear() < 1951) return "—";
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function stopQueueRowOpen(event: { stopPropagation: () => void }): void {
  event.stopPropagation();
}

function ConfigurationQueueRow({
  item,
  hubWritesAllowed,
  operators,
  viewerUserId,
}: {
  item: ConfigurationQueueItem;
  hubWritesAllowed: boolean;
  operators: ReturnType<typeof filterConfigurationOperatorsForQueueItem>;
  viewerUserId: string | null;
}): JSX.Element {
  const navigate = useNavigate();
  const configPath =
    item.kind === "alternate_order" && item.alternateOrderId != null
      ? configurationAlternateOrderPath(item.alternateOrderId)
      : configurationBuildPath(item.batchItemId ?? 0);

  const batchDetailsPath =
    item.batchId != null ? buildManufacturingBatchDetailsPath(item.batchId) : null;

  const openConfiguration = (): void => {
    void navigate(configPath);
  };

  return (
    <li
      role="link"
      tabIndex={0}
      aria-label={`Open configuration for ${item.sku}`}
      className="flex cursor-pointer flex-col gap-2 px-4 py-3 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-400 sm:flex-row sm:items-center sm:gap-4"
      onClick={openConfiguration}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openConfiguration();
        }
      }}
    >
      <div className="min-h-[2.75rem] w-full shrink-0 sm:w-[38%] sm:max-w-xl">
        <p className="line-clamp-2 font-mono text-base font-semibold leading-snug text-slate-900 sm:text-lg">
          {item.sku}
        </p>
      </div>
      <div className="min-w-0 flex-1 space-y-0.5 text-sm">
        <p className="text-slate-600">
          {configurationQueueRowRemainingLine(
            item.remainingToBuild,
            formatMfrShipByDate(item.mfrShipByDate),
            item.inventoryAlternatesOnHand ?? 0,
          )}
        </p>
        <div className="flex min-h-[1.25rem] flex-wrap items-center gap-1.5">
          {item.kind === "alternate_order" ? (
            <span className="font-mono text-brand-700">Alternate order {item.alternateOrderId}</span>
          ) : batchDetailsPath != null ? (
            <Link
              to={batchDetailsPath}
              className="font-mono text-brand-700 hover:text-brand-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              onClick={stopQueueRowOpen}
            >
              Batch {item.batchId}
            </Link>
          ) : null}
          {item.kind === "batch_line" && item.batchId != null ? (
            <span onClick={stopQueueRowOpen} onKeyDown={stopQueueRowOpen}>
              <BatchTeamNotesGlyph
                batchId={item.batchId}
                teamNoteCount={item.teamNoteCount}
                latestTeamNote={item.latestTeamNote}
                hubWritesAllowed={hubWritesAllowed}
              />
            </span>
          ) : null}
          {item.kind === "alternate_order" && item.batchId != null ? (
            <>
              <span className="text-slate-400">·</span>
              <Link
                to={buildManufacturingBatchDetailsPath(item.batchId)}
                className="font-mono text-brand-700 hover:text-brand-900 hover:underline"
                onClick={stopQueueRowOpen}
              >
                Batch {item.batchId}
              </Link>
            </>
          ) : null}
        </div>
      </div>
      <PrePackCartonOperatorStack operators={operators} viewerUserId={viewerUserId} className="shrink-0" />
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center text-slate-400" aria-hidden>
        <ChevronRight className="h-4 w-4" />
      </span>
    </li>
  );
}
